import type { PeriodResidueBin, PrimaryAxisVolumeBin } from '../../../serverTypes';
import { theme } from '../../../theme';
import { failedText, unclassifiedText } from '../residueWords';
import type { TimeBin } from './KalenderwocheBarChart';
import type { useCategoryMeta } from '../../../context/ClassificationSchemaContext';

// <purpose>The ONE place the VOLUME charts decide what is a stack series and what the bar total is, once the
// unclassified value has left them.</purpose>
// <constraints>
//   part 7 round 2, 2026-10-13 (operator ruling): the unclassified value is the lack of a category, not a
//   category — it is in NO series and NO legend of a volume chart. The bar total is therefore the CLASSIFIED
//   volume only; the residue (failed + without a category) is never a series or a segment — part 7 round 5,
//   2026-10-14 (operator-accepted screen): it rides each bar's label as a second small line „+f +w" (red · grey),
//   read from the served stats.perPeriodResidue matched by binLabel; absent list ⇒ no „+n" (never derived).
//   The axis is the classified bins UNION the residue bins (withResidueBins / withResidueDays): a residue-only
//   period stands at „0" with its „+n" line. Keyed to the served `excludedFromEvaluation` marker via `cm.isUnclassified` — never a value id.
//   Takes the resolved vocabulary-meta object (`cm`), the same one every stats surface already holds.
// </constraints>

type StatsMeta = ReturnType<typeof useCategoryMeta>;

/** The stacked series of a volume chart: the served category keys minus the unclassified value. */
export const volumeStackKeys = (cm: StatsMeta): string[] =>
  cm.categoryKeys.filter(k => !cm.isUnclassified(k));

/** Per-bin CLASSIFIED totals: the bin's served total minus the rows of the unclassified value. */
export const classifiedBinTotals = (
  data: TimeBin[],
  rawBins: PrimaryAxisVolumeBin[] | undefined,
  cm: StatsMeta,
): number[] => {
  const dropped = new Map<string, number>();
  for (const b of rawBins ?? []) {
    if (cm.isUnclassified(b.primaryAxis)) dropped.set(b.binLabel, (dropped.get(b.binLabel) ?? 0) + b.count);
  }
  return data.map(d => d.count - (dropped.get(d.label) ?? 0));
};

export type BinResidue = { failed: number; withoutPrimaryAxis: number };

/** The served per-bin residue keyed by binLabel; undefined when the backend serves none (no „+n" anywhere). */
export const residueByBinLabel = (served: PeriodResidueBin[] | undefined): Map<string, BinResidue> | undefined => {
  if (!served) return undefined;
  // Bins sharing a label are SUMMED (KW labels carry no year), as periodVolume / kPerBin sum the category bins.
  const map = new Map<string, BinResidue>();
  for (const r of served) {
    const acc = map.get(r.binLabel) ?? { failed: 0, withoutPrimaryAxis: 0 };
    map.set(r.binLabel, { failed: acc.failed + r.failed, withoutPrimaryAxis: acc.withoutPrimaryAxis + r.withoutPrimaryAxis });
  }
  return map;
};

export const hasResidue = (r: BinResidue | undefined): r is BinResidue =>
  !!r && (r.failed > 0 || r.withoutPrimaryAxis > 0);

/** ECharts rich styles of the residue line: `f` = Fehler, `w` = without a category (the unclassified hue). */
export const RESIDUE_LABEL_RICH = {
  f: { color: theme.statusStrip.error, fontSize: 9, fontWeight: 600 },
  w: { color: theme.statusStrip.unclassified, fontSize: 9, fontWeight: 600 },
} as const;

/** A volume chart tooltip's residue lines (HTML): the words with their counts in the residue hues; '' when none. */
export const residueTooltipLines = (r: BinResidue | undefined, cm: StatsMeta): string => !hasResidue(r) ? '' : [
  r.failed > 0 ? `<div style="font-size:10px;color:${theme.statusStrip.error}">${failedText(r.failed)}</div>` : '',
  r.withoutPrimaryAxis > 0 ? `<div style="font-size:10px;color:${theme.statusStrip.unclassified}">${unclassifiedText(cm, r.withoutPrimaryAxis) ?? ''}</div>` : '',
].join('');

/** A bar's label: the classified total; with residue, a second line „+f +w" (a zero omits its „+n"). */
export const barLabelText = (total: number, r: BinResidue | undefined): string => {
  if (!hasResidue(r)) return String(total);
  const parts = [r.failed > 0 ? `{f|+${r.failed}}` : null, r.withoutPrimaryAxis > 0 ? `{w|+${r.withoutPrimaryAxis}}` : null];
  return `${total}\n${parts.filter(Boolean).join(' ')}`;
};

/** A served dailyVolume ticketDate ("yyyy-mm-dd…") as the daily bins' binLabel (dd.mm.yyyy) — string slicing, no timezone. */
export const dayBinLabel = (ticketDate: string): string =>
  `${ticketDate.slice(8, 10)}.${ticketDate.slice(5, 7)}.${ticketDate.slice(0, 4)}`;

/** The bar axis: the classified bins UNION the residue bins (a residue-only period stands at 0 with its „+n"),
 *  ordered by binStart. Absent residue list ⇒ the classified bins unchanged. */
export const withResidueBins = (data: TimeBin[], served: PeriodResidueBin[] | undefined): TimeBin[] => {
  if (!served) return data;
  const known = new Set(data.map(d => d.label));
  const extra = served.filter(r => !known.has(r.binLabel)).map(r => ({ label: r.binLabel, count: 0, binStart: r.binStart }));
  if (extra.length === 0) return data;
  return [...data, ...extra].sort((a, b) => (a.binStart ?? '').localeCompare(b.binStart ?? ''));
};

type DayBin = { date: string; count: number; binLabel?: string };
const dayKey = (binLabel: string | undefined) =>
  binLabel ? `${binLabel.slice(6, 10)}-${binLabel.slice(3, 5)}-${binLabel.slice(0, 2)}` : '';

/** Tag.Balken's axis: the served days UNION the residue days (dd.mm.yyyy), in date order; absent list ⇒ unchanged. */
export const withResidueDays = <T extends DayBin>(data: T[], served: PeriodResidueBin[] | undefined): DayBin[] => {
  if (!served) return data;
  const known = new Set(data.map(d => d.binLabel));
  const extra = served.filter(r => !known.has(r.binLabel)).map(r => ({ date: r.binLabel.slice(0, 5), count: 0, binLabel: r.binLabel }));
  if (extra.length === 0) return data;
  return [...data, ...extra].sort((a, b) => dayKey(a.binLabel).localeCompare(dayKey(b.binLabel)));
};
